import axios from "axios";
import * as userService from '../utils/userService';

/**
 * HTTP Service - Handles all API calls with proper error handling
 * Follows the same pattern as other services in the codebase (like PrivilegeService)
 * 
 * This service maintains backward compatibility with existing code that expects
 * errors to be returned in the .then() handler rather than .catch()
 */
const HttpService = {
    /**
     * Get default headers for API requests
     * @returns {Object} Headers object with authentication token
     */
    getDefaultHeaders() {
        const token = userService.GetUserToken();
        
        // Log warning if token is missing or invalid (helpful for debugging)
        if (!token || token === true) {
            console.warn('HttpService: AccessToken is missing or invalid. Request may fail.');
        }
        
        return {
            'Content-Type': 'application/json',
            'AccessToken': token || '',
            'pragma': 'no-cache'
        };
    },

    /**
     * Handle 401 Unauthorized errors
     * Clears cookies and redirects to login if unauthorized
     * @param {Object} error - The error object from axios
     */
    handleUnauthorized(error) {
        if (error && error.response && error.response.status === 401) {
            const errorMessage = error.response?.data?.message;
            if (errorMessage === "Unauthorized") {
                console.warn('HttpService: 401 Unauthorized - Clearing session and redirecting to login');
                userService.ClearCookies();
            }
        }
    },

    /**
     * Handle 500 Internal Server errors
     * Logs detailed error information for debugging
     * @param {Object} error - The error object from axios
     */
    handleServerError(error) {
        if (error && error.response && error.response.status === 500) {
            console.error('HttpService: 500 Internal Server Error detected');
            console.error('Error details:', {
                status: error.response.status,
                statusText: error.response.statusText,
                url: error.config?.url,
                method: error.config?.method,
                data: error.response.data,
                message: error.message
            });
        }
    },

    /**
     * Log error for debugging purposes
     * @param {Object} error - The error object from axios
     * @param {string} method - HTTP method (GET, POST, etc.)
     * @param {string} url - API endpoint URL
     */
    logError(error, method, url) {
        if (error && error.response) {
            // Server responded with error status
            console.error(`HttpService ${method} Error [${error.response.status}]:`, {
                url: url,
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data
            });
        } else if (error && error.code) {
            // Network error or other axios error
            console.error(`HttpService ${method} Network Error [${error.code}]:`, {
                url: url,
                code: error.code,
                message: error.message
            });
        }
    },

    /**
     * Perform GET request
     * @param {string} url - API endpoint URL
     * @param {Object} params - Query parameters
     * @returns {Promise} Axios promise that resolves with response or error object
     */
    Get(url, params) {
        return axios({
            method: 'get',
            url: url,
            params: params,
            headers: this.getDefaultHeaders()
        })
        .then((response) => {
            // Return successful response
            return response;
        })
        .catch((error) => {
            // Log error for debugging
            this.logError(error, 'GET', url);
            
            // Handle specific error types
            this.handleUnauthorized(error);
            this.handleServerError(error);
            
            // Return error object for caller to handle (backward compatibility)
            // Callers can check:
            // - error.code (ERR_NETWORK, ERR_BAD_REQUEST, ERR_BAD_RESPONSE, etc.)
            // - error.response.status (401, 500, etc.)
            // - error.response.data (server error message)
            return error;
        });
    },

    /**
     * Perform POST request
     * @param {string} url - API endpoint URL
     * @param {Object} data - Request body data
     * @returns {Promise} Axios promise that resolves with response or error object
     */
    Post(url, data) {
        return axios({
            method: 'post',
            url: url,
            data: data,
            headers: this.getDefaultHeaders()
        })
        .then((response) => {
            // Return successful response
            return response;
        })
        .catch((error) => {
            // Log error for debugging
            this.logError(error, 'POST', url);
            
            // Handle specific error types
            this.handleUnauthorized(error);
            this.handleServerError(error);
            
            // Return error object for caller to handle (backward compatibility)
            // Callers can check:
            // - error.code (ERR_NETWORK, ERR_BAD_REQUEST, ERR_BAD_RESPONSE, etc.)
            // - error.response.status (401, 500, etc.)
            // - error.response.data (server error message)
            return error;
        });
    },

    /**
     * Perform request with custom headers
     * Allows adding additional headers like SecretKey for special endpoints
     * @param {string} method - HTTP method (get, post, put, delete)
     * @param {string} url - API endpoint URL
     * @param {Object} data - Request data (params for GET, body for POST/PUT)
     * @param {Object} customHeaders - Additional headers to include
     * @returns {Promise} Axios promise that resolves with response or error object
     */
    CallWithHeaders(method, url, data = {}, customHeaders = {}) {
        // Merge default headers with custom headers
        const headers = {
            ...this.getDefaultHeaders(),
            ...customHeaders
        };

        // Build axios configuration
        const axiosConfig = {
            method: method.toLowerCase(),
            url: url,
            headers: headers,
        };

        // For GET requests, use params; for others, use data
        if (method.toLowerCase() === 'get') {
            axiosConfig.params = data;
        } else {
            axiosConfig.data = data;
        }

        return axios(axiosConfig)
            .then((response) => {
                // Return successful response
                return response;
            })
            .catch((error) => {
                // Log error for debugging
                this.logError(error, method.toUpperCase(), url);
                
                // Handle specific error types
                this.handleUnauthorized(error);
                this.handleServerError(error);
                
                // Return error object for caller to handle (backward compatibility)
                // Callers can check:
                // - error.code (ERR_NETWORK, ERR_BAD_REQUEST, ERR_BAD_RESPONSE, etc.)
                // - error.response.status (401, 500, etc.)
                // - error.response.data (server error message)
                return error;
            });
    }
};

// Export individual methods for backward compatibility with existing code
// This allows: httpService.Get(), httpService.Post(), httpService.CallWithHeaders()
export const Get = (url, params) => HttpService.Get(url, params);
export const Post = (url, data) => HttpService.Post(url, data);
export const CallWithHeaders = (method, url, data, customHeaders) => 
    HttpService.CallWithHeaders(method, url, data, customHeaders);

// Export the service object as well for potential future advanced usage
export default HttpService;
